/**
 * HROrgChart.tsx — HR / Org Chart (E360-3): reporting lines drawn from the
 * real manager field (employees.manager_id, 0114), the mockup's own
 * precondition — its DEMO-DATA note said "add that field before wiring this
 * up" (klaxiora-crm-mockup-full.html:2186-2190). Layout is the mockup's
 * org-chart geometry; colours are S-Tier tokens (orgchart.css).
 *
 * One fetchDirectory() call builds the whole forest (the E360-3 list rider
 * put managerId on the list wire). The tree rules — forest + unassigned
 * section, leavers only while they hold reports, cycle/depth guards, the
 * no-fabricated-root pin — live in orgChart.ts where vitest pins them.
 *
 * Node content is mockup parity EXACTLY: avatar initials, name, title.
 * No department, no report count — and provably no salary/DOB/NI (the
 * disclosure test, the Field Team precedent).
 */
import { SectionTitle, Avatar, LoadingState, ErrorState, EmptyState } from '../../DashboardShared'
import { useAsync, fetchDirectory } from '../../api'
import { buildOrgForest, type OrgEmployee, type OrgNode } from './orgChart'

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many
}

/** The list fetch's cap (api.ts fetchDirectory limit) — when the roster
 *  comes back exactly this long, rows past the cap may exist and a missing
 *  row hides a WHOLE SUBTREE, so the page says so rather than drawing a
 *  quietly incomplete tree. */
const FETCH_CAP = 200

function NodePill({ e }: { e: OrgEmployee }) {
  return (
    <div className="org-node" data-leaver={e.status === 'leaver' || undefined}>
      <Avatar name={e.fullName} size={34} />
      <div className="min-w-0">
        <div className="org-name text-ink-primary k-truncate">
          {e.fullName}
          {e.status === 'leaver' && (
            <span className="text-overline text-ink-muted ml-2 uppercase">Leaver</span>
          )}
        </div>
        {e.jobTitle && <div className="org-role k-truncate">{e.jobTitle}</div>}
      </div>
    </div>
  )
}

function Tree({ node }: { node: OrgNode }) {
  return (
    <div className="org-chart">
      <NodePill e={node.employee} />
      {node.children.length > 0 && (
        <>
          <div className="org-connector" />
          <div className="org-row" data-single={node.children.length === 1 || undefined}>
            {node.children.map(c => (
              <div key={c.employee.id} className="org-branch">
                <Tree node={c} />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export default function HROrgChart() {
  const { data, loading, error } = useAsync(() => fetchDirectory(), [])

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  const employees = data ?? []
  const forest = buildOrgForest(employees)

  return (
    <div className="page-enter flex flex-col gap-4 mx-auto" style={{ maxWidth: 1120, minWidth: 920 }}>
      <SectionTitle title="Org Chart" subtitle="Reporting lines across the company" />

      {/* Honesty lines — each renders only when its condition is real. */}
      {employees.length >= FETCH_CAP && (
        <div className="text-12 text-ink-muted">
          Showing the first {FETCH_CAP} employees — the chart may be incomplete, and a missing
          person hides everyone who reports to them.
        </div>
      )}
      {forest.guardTripped && (
        <div className="text-12 text-ink-muted">
          Some reporting lines loop back on themselves; the chart stops at the loop rather than
          repeating people. Fix the manager fields to untangle it.
        </div>
      )}

      {employees.length === 0 ? (
        <div className="k-card">
          <EmptyState message="No employees yet"
            hint="The org chart draws itself from the employee records and their manager fields." />
        </div>
      ) : (
        <>
          {forest.roots.length > 0 && (
            <div className="k-card">
              <div className="org-scroll flex flex-col items-center gap-10">
                {forest.roots.map(r => <Tree key={r.employee.id} node={r} />)}
              </div>
            </div>
          )}

          {forest.unassigned.length > 0 && (
            <div className="k-card">
              <div className="k-card-head">
                <div>
                  <div className="k-card-title">No manager recorded</div>
                  <div className="k-card-sub">
                    {forest.unassigned.length} {plural(forest.unassigned.length, 'person', 'people')} without
                    a reporting line — set a manager on the employee record to place them.
                  </div>
                </div>
              </div>
              <div className="p-4 flex flex-wrap gap-3">
                {forest.unassigned.map(e => <NodePill key={e.id} e={e} />)}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
